// Money-format helpers. Values come from the backend as Decimal strings
// (to dodge float rounding); only convert at the display boundary.
//
// A global toggle in the app header (`FormatToggle` in
// NumberFormatContext) switches between three modes:
//
//   "full" — default. `fmtRupees` renders full Indian digits (₹86,16,515);
//            `fmtRupeesShort` auto-picks L / cr / plain based on scale
//            for narrow tiles.
//   "l"    — Every ₹ value renders in Lakhs (`₹86.17 L`). Constant unit
//            — no auto-switching to cr. Very small values collapse to
//            0.00 L (accepted trade-off; the operator picked this mode).
//   "mn"   — Every ₹ value renders in Millions (`₹8.62 Mn`). Constant
//            unit — no Bn.
//
// `fmtRupees` and `fmtRupeesShort` both consult `getCurrentFormatMode()`
// on every call, so the switch takes effect everywhere without a page
// reload (after re-render — see NumberFormatContext for the re-render
// plumbing).
//
// Export flow (CSV / XLSX) is unaffected — every DataTable column
// carries an `exportValue` / `sortAccessor` that returns the raw
// number, and the exporter routes through those in preference to the
// formatted `cell` output. That way a spreadsheet reader gets a
// summable number, not a "₹86.17 L" string.
//
// Per chakra_dashboard#11, extended by #141.

import { getCurrentFormatMode } from "@/components/NumberFormatContext";

const INR = new Intl.NumberFormat("en-IN", {
  style: "currency",
  currency: "INR",
  // Whole rupees only — paise are noise at every scale we display.
  // Use fmtPct (which keeps 2 decimals) for percentages.
  minimumFractionDigits: 0,
  maximumFractionDigits: 0,
});

const PCT = new Intl.NumberFormat("en-IN", {
  style: "percent",
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

export const DASH = "—";

function toNum(v: string | number | null | undefined): number | null {
  if (v === null || v === undefined) return null;
  const n = typeof v === "string" ? Number(v) : v;
  return Number.isFinite(n) ? n : null;
}

/** Force ₹X.XX L on the value, regardless of scale. Used by the "l"
 *  toggle mode via `fmtRupees` / `fmtRupeesShort`. */
function inLakhs(n: number): string {
  const sign = n < 0 ? "-" : "";
  const abs = Math.abs(n);
  return `${sign}₹${(abs / 1e5).toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })} L`;
}

/** Force ₹X.XX Mn on the value, regardless of scale. Used by the "mn"
 *  toggle mode via `fmtRupees` / `fmtRupeesShort`. */
function inMillions(n: number): string {
  const sign = n < 0 ? "-" : "";
  const abs = Math.abs(n);
  return `${sign}₹${(abs / 1e6).toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })} Mn`;
}

/** Canonical money formatter — `₹86,16,515`. Whole rupees only in Full
 *  mode. In "l" / "mn" modes: constant-unit ₹X.XX L / ₹X.XX Mn.
 *  For per-instrument prices where paise matter (settle, LTP, strike),
 *  use `fmtPrice`. */
export function fmtRupees(v: string | number | null | undefined): string {
  const n = toNum(v);
  if (n === null) return DASH;
  const mode = getCurrentFormatMode();
  if (mode === "l")  return inLakhs(n);
  if (mode === "mn") return inMillions(n);
  return INR.format(n);
}

/** Per-instrument price — `₹553.15`. Always 2 decimals. Distinct from
 *  `fmtRupees` (whole rupees for aggregates) because for prices at
 *  ~₹500-1000 the paise are the resolution — dropping them loses the
 *  1-tick signal. Use for settle/LTP/strike, not P&L totals.
 *  Not affected by the format toggle — the resolution is per-instrument. */
export function fmtPrice(v: string | number | null | undefined): string {
  const n = toNum(v);
  if (n === null) return DASH;
  return `₹${n.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

/** Compact form for narrow KPI tiles.
 *
 *  In Full mode: auto-shortens — ₹8.62 cr / ₹86.17 L / else INR.format.
 *  In L mode:   constant ₹X.XX L (same as fmtRupees; tiles must stay narrow).
 *  In Mn mode:  constant ₹X.XX Mn.
 *
 *  The auto behavior in Full mode is the pre-#141 shape — the tile
 *  can't afford full digits, so we shorten regardless of the aggregate
 *  toggle. In L/Mn modes the toggle already picked a constant unit, so
 *  fmtRupeesShort just mirrors fmtRupees. */
export function fmtRupeesShort(v: string | number | null | undefined): string {
  const n = toNum(v);
  if (n === null) return DASH;
  const mode = getCurrentFormatMode();
  if (mode === "l")  return inLakhs(n);
  if (mode === "mn") return inMillions(n);
  // Full mode: auto L/cr on wide values, INR.format on small ones.
  const sign = n < 0 ? "-" : "";
  const abs = Math.abs(n);
  if (abs >= 1e7)
    return `${sign}₹${(abs / 1e7).toLocaleString("en-IN", { maximumFractionDigits: 2, minimumFractionDigits: 2 })} cr`;
  if (abs >= 1e5)
    return `${sign}₹${(abs / 1e5).toLocaleString("en-IN", { maximumFractionDigits: 2, minimumFractionDigits: 2 })} L`;
  return INR.format(n);
}

/** Signed delta: returns `+₹X` for positive, `-₹X` for negative.
 *  Uses INR.format regardless of mode — the sign is the point, not the unit. */
export function fmtRupeesSigned(v: string | number | null | undefined): string {
  const n = toNum(v);
  if (n === null) return DASH;
  return (n > 0 ? "+" : "") + INR.format(n);
}

/** Backend sends 9.4245 (percent), so divide by 100 before Intl.NumberFormat. */
export function fmtPct(v: string | number | null | undefined): string {
  const n = toNum(v);
  return n === null ? DASH : PCT.format(n / 100);
}

/** Plain decimal — `1,23,456.78`. Use for non-currency tabular numbers. */
export function fmtDecimal(
  v: string | number | null | undefined,
  fractionDigits = 2,
): string {
  const n = toNum(v);
  if (n === null) return DASH;
  return n.toLocaleString("en-IN", {
    minimumFractionDigits: fractionDigits,
    maximumFractionDigits: fractionDigits,
  });
}

/** Lot count display. Integer with Indian grouping for |x| ≥ 1; one
 *  decimal place for sub-lot fractions so a real but tiny directional
 *  delta (e.g. −0.4 lots from a 50-share leg with δ ≈ −0.4) doesn't
 *  collapse to 0 and contradict the SHORT/LONG classification. */
export function fmtLots(v: string | number | null | undefined): string {
  const n = toNum(v);
  if (n === null) return DASH;
  if (n === 0) return "0";
  if (Math.abs(n) < 1) {
    return n.toLocaleString("en-IN", { minimumFractionDigits: 1, maximumFractionDigits: 1 });
  }
  return Math.round(n).toLocaleString("en-IN");
}

/** Plain integer count — `12,34,567`. */
export function fmtInt(v: string | number | null | undefined): string {
  const n = toNum(v);
  if (n === null) return DASH;
  return Math.round(n).toLocaleString("en-IN");
}

export function fmtDate(v: string | null | undefined): string {
  if (!v) return DASH;
  return v;
}

/** NIFTY cycle / expiry month label: `2026-06` → `2026JUN` (#14).
 *  Backend keeps the canonical `YYYY-MM` shape (sortable + locale-independent);
 *  this helper renders it at the display boundary only. Sort accessors must
 *  continue to use the raw string so chronological order holds. */
const _MONTH_3LETTER = ["JAN","FEB","MAR","APR","MAY","JUN","JUL","AUG","SEP","OCT","NOV","DEC"];
export function fmtCycleLabel(v: string | null | undefined): string {
  if (!v) return DASH;
  const m = /^(\d{4})-(\d{2})$/.exec(v);
  if (!m) return v;  // unexpected shape — pass through
  const mi = Number(m[2]) - 1;
  if (mi < 0 || mi > 11) return v;
  return `${m[1]}${_MONTH_3LETTER[mi]}`;
}

/** P&L direction class — emerald for positive, rose for negative. */
export function pnlClass(v: string | number | null | undefined): string {
  const n = toNum(v);
  if (n === null || n === 0) return "text-zinc-500";
  return n > 0 ? "text-emerald-400" : "text-rose-400";
}
